import React, { useState } from "react";
import { Box, Button, TextField } from "@mui/material";
import { motion } from "framer-motion";
import { Phone, Calendar } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import validator from "validator";
import sh from "bundles/common/utils/sh";
import JulesPhoto from "pictures/picture-jules.jpg";
import SebastienPhoto from "pictures/picture-sebastien.jpg";

const ContactSection = ({ type = "recruiter" }) => {
  const isCandidate = type === "candidate";
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const currentCandidate = useSelector((state) => state.current_candidate);
  const currentRecruiter = useSelector((state) => state.current_recruiter);
  const i18nLocale = useSelector((state) => state.i18nLocale);

  const team = [
    {
      nameKey: "landing_recruiter.picture_jules",
      phone: "+32 493 48 32 84",
      tel: "0032493483284",
      demo: "https://meetings-eu1.hubspot.com/meetings/jules-fadie",
      demoLabelKey: "landing_recruiter.contact_form.book_demo_nl",
      initials: "JF",
      photo: JulesPhoto,
    },
    {
      nameKey: "landing_recruiter.picture_sebastien",
      phone: "+32 492 93 86 64",
      tel: "0032492938664",
      demo: "https://meetings-eu1.hubspot.com/meetings/sebastien-del-marmol",
      demoLabelKey: "landing_recruiter.contact_form.book_demo_fr",
      initials: "SM",
      photo: SebastienPhoto,
    },
  ];

  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const validate = (fieldValues = { email, phone, message }) => {
    const temp = { ...errors };
    const required = intl.formatMessage({ id: "form_validation.required" });
    const notAnEmail = intl.formatMessage({ id: "form_validation.not_an_email" });

    if ("email" in fieldValues) {
      temp.email = fieldValues.email ? "" : required;
      if (fieldValues.email && !validator.isEmail(fieldValues.email)) {
        temp.email = notAnEmail;
      }
    }
    if ("phone" in fieldValues) {
      temp.phone = fieldValues.phone ? "" : required;
    }

    setErrors({ ...temp });
    return Object.values(temp).every((x) => x === "");
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    setIsSubmitted(true);

    if (validate({ email, phone, message })) {
      const params = {
        email,
        phone,
        content: message,
        preferred_locale:
          currentCandidate?.preferred_locale ||
          currentRecruiter?.preferred_locale,
        locale: i18nLocale,
      };
      sh.post("contact-us", { data: params })
        .then(() => {
          setIsSuccess(true);
          enqueueSnackbar(
            intl.formatMessage({
              id: "landing_recruiter.contact_form.contact_success",
            }),
            { variant: "success" }
          );
          setEmail("");
          setPhone("");
          setMessage("");
        })
        .catch(() => {
          enqueueSnackbar(
            intl.formatMessage({ id: "flash.unknown" }),
            { variant: "error" }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      bgcolor: "hsl(0, 0%, 100%)",
      borderRadius: "0.5rem",
      fontFamily: "Segoe UI Regular, system-ui, sans-serif",
      fontSize: "0.875rem",
      "& fieldset": { border: "none" },
      "&:hover fieldset": { border: "none" },
      "&.Mui-focused fieldset": {
        border: "2px solid hsl(145, 63%, 42%)",
      },
    },
    "& .MuiOutlinedInput-input": {
      color: "hsl(220, 25%, 12%)",
      "&::placeholder": {
        color: "hsla(220, 10%, 46%, 0.5)",
        opacity: 1,
      },
    },
  };

  return (
    <Box
      component="section"
      id="contact"
      sx={{
        py: { xs: "32px", md: "48px" },
        px: { xs: "16px", sm: "24px", lg: "32px" },
        bgcolor: "hsl(178, 93%, 10%)",
        borderBottom: "1px solid hsl(0 0% 100% / 0.1)",
        overflow: "hidden",
      }}
    >
      <Box sx={{ maxWidth: "1152px", mx: "auto" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gap: "48px",
            alignItems: "flex-start",
          }}
        >
          {/* Left: Title + Team */}
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Box
              component="h2"
              sx={{
                fontSize: { xs: "1.875rem", md: "2.25rem" },
                fontFamily: "Segoe UI Black, system-ui, sans-serif",
                color: "hsl(0, 0%, 98%)",
                letterSpacing: "-0.025em",
                mb: "16px",
                mt: 0,
              }}
            >
              <FormattedMessage
                id={isCandidate ? "landing_candidate.contact" : "landing_recruiter.contact"}
                values={{ b: (chunks) => <>{chunks}</> }}
              />
            </Box>
            <Box
              component="p"
              sx={{
                color: "hsla(0, 0%, 98%, 0.5)",
                fontSize: "1rem",
                fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                maxWidth: "448px",
                mb: "40px",
                mt: 0,
              }}
            >
              {isCandidate ? (
                <>
                  <FormattedMessage id="landing_candidate.contact_sub1" />
                  {" "}
                  <FormattedMessage id="landing_candidate.contact_sub2" />
                </>
              ) : (
                <FormattedMessage id="landing_recruiter.question_subtitle" />
              )}
            </Box>

            {/* Team members — recruiter only */}
            {!isCandidate && (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: "24px",
                }}
              >
                {team.map((person, i) => (
                  <Box
                    key={i}
                    component={motion.div}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: i * 0.1 }}
                    sx={{ textAlign: "left" }}
                  >
                    <Box
                      component="img"
                      src={person.photo}
                      alt={intl.formatMessage({ id: person.nameKey })}
                      sx={{
                        width: "112px",
                        height: "112px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid hsla(0, 0%, 98%, 0.2)",
                        mb: "12px",
                        display: "block",
                      }}
                    />
                    <Box
                      component="p"
                      sx={{
                        fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                        color: "hsl(0, 0%, 98%)",
                        fontSize: "1rem",
                        mb: "8px",
                        mt: 0,
                      }}
                    >
                      <FormattedMessage id={person.nameKey} />
                    </Box>
                    <Box
                      component="a"
                      href={`tel:${person.tel}`}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "0.875rem",
                        color: "hsla(0, 0%, 98%, 0.5)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        textDecoration: "none",
                        mb: "8px",
                        transition: "color 0.2s",
                        "&:hover": { color: "hsl(145, 63%, 42%)" },
                      }}
                    >
                      <Phone size={13} /> {person.phone}
                    </Box>
                    <Box
                      component="a"
                      href={person.demo}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        fontSize: "0.875rem",
                        color: "hsla(0, 0%, 98%, 0.5)",
                        fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                        textDecoration: "none",
                        transition: "color 0.2s",
                        "&:hover": { color: "hsl(145, 63%, 42%)" },
                      }}
                    >
                      <Calendar size={13} />{" "}
                      <FormattedMessage id={person.demoLabelKey} />
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>

          {/* Right: Contact Form */}
          {!isSuccess ? (
            <Box
              component={motion.form}
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.15 }}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              {/* Email */}
              <Box>
                <Box
                  component="label"
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsl(0, 0%, 98%)",
                    mb: "6px",
                    display: "block",
                  }}
                >
                  <FormattedMessage
                    id="landing_recruiter.email_description"
                    values={{ b: (chunks) => <>{chunks}</> }}
                  />
                </Box>
                <TextField
                  type="email"
                  placeholder={intl.formatMessage({
                    id: "landing_recruiter.contact_form.email_address",
                  })}
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (isSubmitted) validate({ email: e.target.value });
                  }}
                  required
                  fullWidth
                  size="small"
                  error={isSubmitted && Boolean(errors.email)}
                  helperText={isSubmitted ? errors.email : ""}
                  inputProps={{ maxLength: 255 }}
                  sx={{
                    ...inputSx,
                    "& .MuiOutlinedInput-root": {
                      ...inputSx["& .MuiOutlinedInput-root"],
                      height: "44px",
                    },
                  }}
                />
              </Box>

              {/* Phone */}
              <Box>
                <Box
                  component="label"
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsl(0, 0%, 98%)",
                    mb: "6px",
                    display: "block",
                  }}
                >
                  <FormattedMessage
                    id="landing_recruiter.phone_description"
                    values={{ b: (chunks) => <>{chunks}</> }}
                  />
                </Box>
                <TextField
                  type="tel"
                  placeholder={intl.formatMessage({
                    id: "landing_recruiter.contact_form.phone",
                  })}
                  value={phone}
                  onChange={(e) => {
                    setPhone(e.target.value);
                    if (isSubmitted) validate({ phone: e.target.value });
                  }}
                  required
                  fullWidth
                  size="small"
                  error={isSubmitted && Boolean(errors.phone)}
                  helperText={isSubmitted ? errors.phone : ""}
                  inputProps={{ maxLength: 20 }}
                  sx={{
                    ...inputSx,
                    "& .MuiOutlinedInput-root": {
                      ...inputSx["& .MuiOutlinedInput-root"],
                      height: "44px",
                    },
                  }}
                />
              </Box>

              {/* Message */}
              <Box>
                <Box
                  component="label"
                  sx={{
                    fontSize: "0.875rem",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    color: "hsl(0, 0%, 98%)",
                    mb: "6px",
                    display: "block",
                  }}
                >
                  <FormattedMessage
                    id="landing_recruiter.message_description"
                    values={{ b: (chunks) => <>{chunks}</> }}
                  />
                </Box>
                <TextField
                  placeholder={intl.formatMessage({
                    id: "landing_recruiter.contact_form.your_question",
                  })}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  fullWidth
                  multiline
                  minRows={4}
                  inputProps={{ maxLength: 1000 }}
                  sx={{
                    ...inputSx,
                    "& .MuiOutlinedInput-root": {
                      ...inputSx["& .MuiOutlinedInput-root"],
                      resize: "none",
                    },
                  }}
                />
              </Box>

              {/* Submit */}
              <Button
                type="submit"
                fullWidth
                disabled={loading}
                sx={{
                  bgcolor: "hsl(153 68% 25%)",
                  color: "white",
                  fontFamily: "Segoe UI Semibold, system-ui, sans-serif",
                  fontWeight: 700,
                  fontSize: "0.875rem",
                  px: "1.5rem",
                  py: "0.875rem",
                  borderRadius: "0.75rem",
                  textTransform: "none",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  "&:hover": {
                    bgcolor: "hsl(153 68% 25% / 0.9)",
                  },
                  "&:disabled": {
                    opacity: 0.7,
                    color: "white",
                  },
                }}
              >
                {loading ? "..." : intl.formatMessage({ id: "send" })}
              </Button>
            </Box>
          ) : (
            <Box
              component={motion.div}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: "300px",
              }}
            >
              <Box
                sx={{
                  textAlign: "center",
                  p: "32px",
                  borderRadius: "1rem",
                  bgcolor: "hsla(0, 0%, 98%, 0.04)",
                  border: "1px solid hsla(0, 0%, 98%, 0.08)",
                }}
              >
                <Box
                  sx={{
                    width: "48px",
                    height: "48px",
                    borderRadius: "50%",
                    bgcolor: "hsla(145, 63%, 42%, 0.15)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mx: "auto",
                    mb: "16px",
                    color: "hsl(145, 63%, 42%)",
                  }}
                >
                  ✓
                </Box>
                <Box
                  component="p"
                  sx={{
                    color: "hsl(0, 0%, 98%)",
                    fontFamily: "Segoe UI Bold, system-ui, sans-serif",
                    fontSize: "1.125rem",
                    mb: "8px",
                    mt: 0,
                  }}
                >
                  <FormattedMessage id="landing_recruiter.contact_form.contact_success" />
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ContactSection;
